/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B030040 請求明細照会
 */
import { Box } from "@mui/material";
import { Column, Query } from "@material-table/core";
import {
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  msgState,
} from "components";
import { labels } from "messages/labels";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { focusState, navigateTypeState, useFocus } from "states";
import { B000150 } from "pages/b000/B000150";
import {
  B000150SeikyuNmr,
  B000150TokuiNmr,
  B020020Get,
  catchRestError,
} from "clients/rest";
import { useNavigate } from "react-router-dom";
import {
  B030040Find,
  B030040FindRequest,
  B030040FindResponse,
} from "clients/rest/b030/B030040";
import { date, dateWithSlashAt, mapIds } from "services";
import { calledParam, editKbn, urinyukinKbn } from "services/codes";
import { B020010InputData } from "pages/b020/B020010";
import { B030050Find } from "clients/rest/b030/B030050";
import { B030050InputData } from "./B030050";
import { getServerError } from "messages";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// 検索条件を格納するデータタイプ
type SearchedFields = {
  searched: boolean;
} & Partial<{
  fields: Fields;
  page: number;
  pageSize: number;
  sort: string[];
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  seikyushimeYmd: string;
  seikyuCd: string;
  seikyushitenCd: string;
  seikyuNmr: string;
  tokuiCd: string;
  tokuishitenCd: string;
  tokuiNmr: string;
  zenseikyuzanKin: string;
  konkainyuKin: string;
  kurikoshiKin: string;
  konkaiuriKin: string;
  henpinnebikiKin: string;
  zeiKbn: string;
  shohizeiGokeiKin: string;
  konkaiseikyuKin: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyo: number;
  urinyukinYmd: string;
  urinyukindenpyoNo: number;
  urinyukinKbn: string;
  uchiwakeNm: string;
  shohinCd: string;
  shohinNm: string;
  urisu: number;
  uritanka: number;
  uriKin: number;
  shohizeiKin: number;
  nyuKin: number;
  tokuiCd: string;
  tokuishitenCd: string;
  tokuiCdView: string;
  tokuiNm: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  seikyushimeYmd: "",
  seikyuCd: "",
  seikyushitenCd: "",
  seikyuNmr: "",
  tokuiCd: "",
  tokuishitenCd: "",
  tokuiNmr: "",
  zenseikyuzanKin: "",
  konkainyuKin: "",
  kurikoshiKin: "",
  konkaiuriKin: "",
  henpinnebikiKin: "",
  zeiKbn: "",
  shohizeiGokeiKin: "",
  konkaiseikyuKin: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyo", title: "行　", width: "3rem", align: "right" },
  {
    field: "urinyukinYmd",
    title: "伝票日付",
    width: "7rem",
    render: (data) => {
      if (data.urinyukinYmd! !== null && data.urinyukinYmd! !== "") {
        return dateWithSlashAt(data.urinyukinYmd!);
      }
    },
  },
  { field: "urinyukindenpyoNo", title: "伝票No.", width: "6rem" },
  { field: "urinyukinKbn", hidden: true },
  { field: "uchiwakeNm", title: "区分", width: "5rem" },
  { field: "shohinCd", title: "商品コード", width: "8rem" },
  { field: "shohinNm", title: "商品名", width: "20rem" },
  {
    field: "urisu",
    title: "数量　",
    width: "5rem",
    align: "right",
    render: (data) => data.urisu?.toLocaleString(),
  },
  {
    field: "uritanka",
    title: "単価　",
    width: "9rem",
    align: "right",
    render: (data) => data.uritanka?.toLocaleString(),
  },
  {
    field: "uriKin",
    title: "金額　",
    width: "9rem",
    align: "right",
    render: (data) => data.uriKin?.toLocaleString(),
  },
  {
    field: "shohizeiKin",
    title: "消費税　",
    width: "9rem",
    align: "right",
    render: (data) => data.shohizeiKin?.toLocaleString(),
  },
  {
    field: "nyuKin",
    title: "入金額　",
    width: "9rem",
    align: "right",
    render: (data) => data.nyuKin?.toLocaleString(),
  },
  { field: "tokuiCd", hidden: true },
  { field: "tokuishitenCd", hidden: true },
  {
    field: "tokuiCdView",
    title: "得意先コード",
    width: "8rem",
    render: (data) => {
      if (!data.tokuiCd || !data.tokuishitenCd) {
        return "";
      } else {
        return data.tokuiCd + "-" + data.tokuishitenCd;
      }
    },
  },
  { field: "tokuiNm", title: "得意先名", width: "8rem" },
];

const focusId = "B030040";

export const B030040 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

// 検索条件を格納するステート
const B030040State = atom<SearchedFields>({
  key: "B030040State",
  default: {
    searched: false,
    fields: defaultFields,
  },
});

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  const [menuNavigate, setMenuNavigate] = useRecoilState(navigateTypeState);
  // 詳細ボタン活性制御
  const [disabledDetailButton, setDisabledDetailButton] = useState(true);
  // 検索条件フィールドの定義
  const [searchedFields, setSearchedFields] = useRecoilState(B030040State);

  // 初期表示イベント
  useEffect(() => {
    // 検索条件が保存されている
    if (searchedFields.searched) {
      const fields = searchedFields.fields;
      if (typeof fields !== "undefined") {
        methods.setValue("seikyushimeYmd", fields.seikyushimeYmd);
        methods.setValue("seikyuCd", fields.seikyuCd);
        methods.setValue("seikyushitenCd", fields.seikyushitenCd);
        methods.setValue("seikyuNmr", fields.seikyuNmr);
        methods.setValue("tokuiCd", fields.tokuiCd);
        methods.setValue("tokuishitenCd", fields.tokuishitenCd);
        methods.setValue("tokuiNmr", fields.tokuiNmr);
        setSearched(true);
        return;
      }
    } else {
      methods.setValue("seikyushimeYmd", date());
    }

    // 初期フォーカス
    focus(1);
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // 検索実行時、選択行の解除と各ボタンの非活性化
    setRowData(undefined);
    setDisabledDetailButton(true);
    (tableRef.current as any).handleClearSelect();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }

      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B030040FindRequest = {
        seikyushimeYmd: fields.seikyushimeYmd!,
        seikyuCd: fields.seikyuCd!,
        seikyushitenCd: fields.seikyushitenCd!,
        tokuiCd: fields.tokuiCd,
        tokuishitenCd: fields.tokuishitenCd,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: [
          "urinyukinYmd,asc",
          "urinyukindenpyoNo,asc",
          "urinyukingyoNo,asc",
          "seikyuCd,asc",
          "seikyushitenCd,asc",
          "seikyushimeYmd,asc",
          "urinyukinKbn,asc",
        ],
      };
      // API呼び出し
      B030040Find(param)
        // 正常取得時
        .then((value) => {
          // 合計値の設定
          handleGokei(value.data.content[0], value.data.totalElements);

          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          // エラー時は合計値をクリアする。
          methods.setValue("zenseikyuzanKin", "");
          methods.setValue("konkainyuKin", "");
          methods.setValue("kurikoshiKin", "");
          methods.setValue("konkaiuriKin", "");
          methods.setValue("henpinnebikiKin", "");
          methods.setValue("zeiKbn", "");
          methods.setValue("shohizeiGokeiKin", "");
          methods.setValue("konkaiseikyuKin", "");
          // 詳細ボタン非活性化
          setDisabledDetailButton(true);
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // 合計値の設定
  const handleGokei = (data: B030040FindResponse, totalElements: number) => {
    // 検索結果が存在するかチェック
    if (totalElements === 0) {
      // データ非存在メッセージ表示
      setMsg(getServerError("B030040_001"));
      // 結果なしの場合、クリア
      methods.setValue("zenseikyuzanKin", "");
      methods.setValue("konkainyuKin", "");
      methods.setValue("kurikoshiKin", "");
      methods.setValue("konkaiuriKin", "");
      methods.setValue("henpinnebikiKin", "");
      methods.setValue("zeiKbn", "");
      methods.setValue("shohizeiGokeiKin", "");
      methods.setValue("konkaiseikyuKin", "");
      // 詳細ボタン非活性化
      setDisabledDetailButton(true);
    }
    // 結果が1件以上取得できた場合
    else {
      // 合計値の設定
      methods.setValue("zenseikyuzanKin", data.zenseikyuzanKin ?? "");
      methods.setValue("konkainyuKin", data.konkainyuKin ?? "");
      methods.setValue("kurikoshiKin", data.kurikoshiKin ?? "");
      methods.setValue("konkaiuriKin", data.konkaiuriKin ?? "");
      methods.setValue("henpinnebikiKin", data.henpinnebikiKin ?? "");
      methods.setValue("zeiKbn", data.zeiKbn ?? "");
      methods.setValue("shohizeiGokeiKin", data.shohizeiGokeiKin ?? "");
      methods.setValue("konkaiseikyuKin", data.konkaiseikyuKin ?? "");
    }
  };

  // テーブル行クリックイベント
  const handleSelectRow = (_: any, rowData: Data | undefined) => {
    setRowData(undefined);
    setRowData(rowData);
    setDisabledDetailButton(false);
  };

  // 詳細ボタンクリックイベント
  const handleClickDatailButton = () => {
    if (typeof rowData === "undefined") {
      return;
    }
    // 売上入金区分をチェック
    if (rowData.urinyukinKbn!.toString() === urinyukinKbn.uriage) {
      // 売上入力(参照モード)画面への遷移処理
      handleUriageNavigate(rowData);
    } else if (rowData.urinyukinKbn!.toString() === urinyukinKbn.nyukin) {
      // 入金入力画面への遷移処理
      handleNyukinNavigate(rowData);
    }
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    // 売上入金区分をチェック
    if (data.urinyukinKbn!.toString() === urinyukinKbn.uriage) {
      // 売上入力(参照モード)画面への遷移処理
      handleUriageNavigate(data);
    } else if (data.urinyukinKbn!.toString() === urinyukinKbn.nyukin) {
      // 入金入力画面への遷移処理
      handleNyukinNavigate(data);
    }
  };

  // 売上入力(参照モード)画面への遷移処理
  const handleUriageNavigate = (rowData: Data) => {
    // 売上データの検索を行う
    B020020Get({ uridenpyoNo: rowData!.urinyukindenpyoNo!.toString() }).then(
      (value) => {
        const inputData: B020010InputData = {
          // 参照モードに設定
          editKbn: editKbn.reference,
          uriage: value.data,
        };
        setSearchedFields({ searched: true, fields: methods.getValues() });
        setMenuNavigate("next");
        navigate("/b020010", { state: inputData });
      }
    );
  };

  // 入金入力画面への遷移処理
  const handleNyukinNavigate = (rowData: Data) => {
    // 入金データの検索を行う
    B030050Find({
      nyukindenpyoNo: rowData!.urinyukindenpyoNo!.toString(),
    })
      .then((value) => {
        const inputData: B030050InputData = {
          nyukin: value.data,
          editKbn: editKbn.update,
        };
        if (
          inputData.nyukin[0].seikyushimeYmd &&
          inputData.nyukin[0].seikyushimeNo
        ) {
          inputData.editKbn = editKbn.reference;
        }
        setSearchedFields({ searched: true, fields: methods.getValues() });
        setMenuNavigate("next");
        navigate("/b030050", { state: inputData });
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="請求明細照会"
        gridTemplateColumns="6rem 6rem 6rem 20rem 5rem"
      >
        <SLabel required>請求締日</SLabel>
        <SDateField
          name="seikyushimeYmd"
          label="請求締日"
          required
          gridColumn="span 2"
          {...register(1)}
        />

        <Box gridColumn="span 2" />

        <SLabel required>請求先</SLabel>
        <SNumberField
          name="seikyuCd"
          label="請求先コード"
          code
          required
          size={6}
          onBlur={() => B000150SeikyuNmr(methods, navigate, setMsg)}
          {...register(2)}
        />
        <SNumberField
          name="seikyushitenCd"
          label="請求先支店コード"
          code
          required
          size={4}
          dialog={<B000150 calledParam={calledParam.seikyu} />}
          handleSubmit={(data: any) => {
            methods.setValue("seikyuCd", data.tokuiCd);
            methods.setValue("seikyushitenCd", data.tokuishitenCd);
            methods.setValue("seikyuNmr", data.tokuiNmr);
          }}
          onBlur={() => B000150SeikyuNmr(methods, navigate, setMsg)}
          {...register(3)}
        />
        <STextField name="seikyuNmr" label="請求先名称" size={40} disabled />

        <Box />

        <SLabel>得意先</SLabel>
        <SNumberField
          name="tokuiCd"
          label="得意先コード"
          code
          size={6}
          onBlur={() => B000150TokuiNmr(methods, navigate, setMsg)}
          {...register(4)}
        />
        <SNumberField
          name="tokuishitenCd"
          label="得意先支店コード"
          code
          size={4}
          dialog={<B000150 calledParam={calledParam.tokui} />}
          handleSubmit={(data: any) => {
            methods.setValue("tokuiCd", data.tokuiCd);
            methods.setValue("tokuishitenCd", data.tokuishitenCd);
            methods.setValue("tokuiNmr", data.tokuiNmr);
          }}
          onBlur={() => B000150TokuiNmr(methods, navigate, setMsg)}
          {...register(5)}
        />
        <STextField name="tokuiNmr" label="得意先名称" size={40} disabled />

        <SNormalButton
          label={labels.buttons.search}
          onClick={handleClickSearchButton}
          align="right"
          {...register(6)}
        />
      </SFrame>

      <SFrame gridTemplateColumns="10rem 10rem 10rem 10rem 10rem 10rem 10rem ">
        <SLabel>前回請求残高</SLabel>
        <SLabel>今回入金額</SLabel>
        <SLabel>繰越金額</SLabel>
        <SLabel>今回売上額</SLabel>
        <SLabel>返品値引額</SLabel>
        <SLabel>消費税</SLabel>
        <SLabel>今回請求額</SLabel>

        <SNumberField
          name="zenseikyuzanKin"
          label="前回請求残高"
          size={9}
          disabled
          align="right"
        />
        <SNumberField
          name="konkainyuKin"
          label="今回入金額"
          size={9}
          disabled
          align="right"
        />
        <SNumberField
          name="kurikoshiKin"
          label="繰越金額"
          size={9}
          disabled
          align="right"
        />
        <SNumberField
          name="konkaiuriKin"
          label="今回売上額"
          size={9}
          disabled
          align="right"
        />
        <SNumberField
          name="henpinnebikiKin"
          label="返品値引額"
          size={9}
          disabled
          align="right"
        />
        <SNumberField
          name="shohizeiGokeiKin"
          label="消費税"
          size={9}
          disabled
          align="right"
        />
        <SNumberField
          name="konkaiseikyuKin"
          label="今回請求額"
          size={9}
          disabled
          align="right"
        />
      </SFrame>

      <SFrame>
        <STable
          keyName="gyo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          onRowClick={handleSelectRow}
          handleDoubleClick={handleDoubleClickTableRow}
          width={1250}
          height={500}
          pageSize={15}
          onPageChange={handleResetSearch}
        />
      </SFrame>

      <SFrame>
        <SNormalButton
          label={labels.buttons.detail}
          onClick={handleClickDatailButton}
          disabled={disabledDetailButton}
          align="right"
          {...register(7)}
        />
      </SFrame>
    </FormProvider>
  );
};
